import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, EmptyBox, KpiCard, PageTitle } from '../../components/admin/ui';
import { MrrChart } from '../../components/admin/MrrChart';
import {
  formatBRLCompact,
  formatBRL,
  formatPct,
  PLANO_LABEL,
  COBRANCA_STATUS_LABEL,
  COBRANCA_STATUS_COLOR,
  mesLabel,
} from '../../lib/format';

export function AdminFinanceiro() {
  const { data, loading, error, reload } = useAsync(() => trpc.admin.financeiro.query());

  return (
    <div className="mx-auto max-w-5xl">
      <PageTitle titulo="Financeiro" />

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}

      {data && data.mrrHistorico.length === 0 && (
        <EmptyBox titulo="Sem dados de receita" descricao="Rode o seed de demonstração." />
      )}

      {data && data.mrrHistorico.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard
              label="MRR"
              valor={formatBRLCompact(data.kpis.mrr)}
              destaque
              sub={<span className="text-accent">+ {formatBRLCompact(data.kpis.mrrNovo)} novo no mês</span>}
            />
            <KpiCard
              label="Churn"
              valor={formatPct(data.kpis.churnPct)}
              sub={`${formatBRLCompact(data.kpis.churnRs)} / mês`}
            />
            <KpiCard label="Receita / cliente" valor={formatBRLCompact(data.kpis.arpa)} sub="ARPA mensal médio" />
            <KpiCard label="Projeção próx. mês" valor={formatBRLCompact(data.kpis.projecao)} sub="MRR + novo" />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            <div className="card p-[26px]">
              <div className="text-[15px] font-semibold text-ink">MRR nos últimos meses</div>
              <div className="mt-0.5 text-xs text-ink-3">
                receita recorrente mensal · acumulado {formatBRLCompact(data.kpis.receitaAcumulada)}
              </div>
              <MrrChart historico={data.mrrHistorico} />
            </div>

            <div className="card p-[26px]">
              <div className="text-[15px] font-semibold text-ink">Clientes por plano</div>
              <div className="mt-5 space-y-4">
                {data.clientesPorPlano.map((p) => {
                  const total = Math.max(data.clientesPorPlano.reduce((a, x) => a + x.clientes, 0), 1);
                  return (
                    <div key={p.plano}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ink-2">{PLANO_LABEL[p.plano]}</span>
                        <span className="num text-ink">{p.clientes}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-2">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(p.clientes / total) * 100}%`, background: 'var(--accent)' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {/* Receita por cliente */}
            <div className="card overflow-hidden">
              <div className="border-b border-line px-5 py-3 text-[15px] font-semibold text-ink">
                Receita por cliente
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-line">
                  {data.receitaPorCliente.map((r) => (
                    <tr key={r.orgId}>
                      <td className="px-5 py-2.5 text-ink">{r.nome}</td>
                      <td className="px-3 py-2.5 text-ink-3">{PLANO_LABEL[r.plano]}</td>
                      <td className="num px-5 py-2.5 text-right text-ink">{formatBRL(r.mrr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cobranças do mês */}
            <div className="card overflow-hidden">
              <div className="flex items-center justify-between border-b border-line px-5 py-3">
                <span className="text-[15px] font-semibold text-ink">Recebimentos</span>
                {data.cobrancasMes.competencia && (
                  <span className="text-xs text-ink-3">{mesLabel(data.cobrancasMes.competencia)}</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 border-b border-line px-5 py-3 text-center">
                {(['pagos', 'pendentes', 'atrasados'] as const).map((k) => {
                  const cor = k === 'pagos' ? 'var(--accent)' : k === 'pendentes' ? 'var(--amber)' : 'var(--risk)';
                  return (
                    <div key={k}>
                      <div className="num text-lg font-semibold" style={{ color: cor }}>
                        {formatBRLCompact(data.cobrancasMes[k].valor)}
                      </div>
                      <div className="text-xs capitalize text-ink-3">
                        {k} · {data.cobrancasMes[k].n}
                      </div>
                    </div>
                  );
                })}
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-line">
                  {data.cobrancasMes.itens.map((c) => (
                    <tr key={c.orgId}>
                      <td className="px-5 py-2.5 text-ink">{c.nome}</td>
                      <td className="num px-3 py-2.5 text-ink-2">{formatBRL(c.valor)}</td>
                      <td className="px-5 py-2.5 text-right">
                        <span className="text-xs font-medium" style={{ color: COBRANCA_STATUS_COLOR[c.status] }}>
                          {COBRANCA_STATUS_LABEL[c.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
